import type { Place } from '@backend/lib-aws-location';
import { Features } from '@backend/lib-feature-mgr';
import type { BusinessUnit } from '@backend/lib-l10n';
import { alpha3ToAlpha2 } from 'i18n-iso-countries';

import { getJobsBusinessUnitApiAddressUrl } from '~/config';
import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import { useCacheFromCallback } from '~/hooks';
import type { AddressWithNameAndLocation } from '~/utils/address';

export const useGetAddressDetails = () => {
  const { businessUnit } = useBusinessUnitContext();

  return useCacheFromCallback(async id => {
    try {
      const response = await fetch(getJobsBusinessUnitApiAddressUrl(businessUnit, 'getDetails'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      const place = (await response.json()) as Place | null;
      if (place) {
        return placeToAddressWithCityAndLocation(businessUnit, place);
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  });
};

export const placeToAddressWithCityAndLocation = (
  businessUnit: BusinessUnit,
  place: Place,
): AddressWithNameAndLocation | null => {
  if (!place.city || !place.location) {
    return null;
  }

  return {
    streetNumber: place.streetNumber,
    streetName: place.streetName,
    street: [place.streetNumber, place.streetName].filter(Boolean).join(' '),
    country: place.countryIso3Code ? alpha3ToAlpha2(place.countryIso3Code) : undefined,
    city: place.city,
    name: place.city,
    zipcode: place.zipcode,
    ...(Features.JobOfferApplyWithState.isEnabled(businessUnit) && { state: place.region }),
    location: {
      latitude: place.location.latitude,
      longitude: place.location.longitude,
    },
  };
};
